import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import MomentPropTypes from "react-moment-proptypes"
import moment from "moment-timezone"
import HelpTooltip from "@swipesense/help-tooltip"
import AutosuggestBar from "../AutosuggestBar"
import CreateEditSubject from "../CreateEditSubject"
import ExcludedLocationsModalEnhanced from "../ExcludedLocationsModal/ExcludedLocationsModalEnhanced"
import "./CreateEditCase.scss"

// This component is non-exported, so no propTypes necessary.
/* eslint-disable react/prop-types */
const InactiveSubject = (props) => {
  const dateFormat = "M/D/YYYY h:mma"

  return (
    <table>
      <tbody>
        <tr>
          <td className="label">ROOM</td>
          <td className="name">{props.subject.entityName}</td>
        </tr>
        <tr>
          <td className="label occupied">OCCUPIED</td>
          <td>
            <div className="start-at">
              {props.subject.startAt?.format(dateFormat)}
            </div>
            {" - "}
            <div className="end-at">
              {props.subject.endAt?.format(dateFormat)}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  )
}
/* eslint-enable react/prop-types */

const CreateEditCase = (props) => {
  // Create a factory for new subject defaults.
  const newSubject = () => ({
    entityName: "",
    entityId: null,
    startAt: moment()
      .tz(props.timezone)
      .subtract(1, "day")
      .set({ hour: 7, minute: 0, second: 0 }),
    endAt: moment()
      .tz(props.timezone)
      .subtract(1, "day")
      .set({ hour: 19, minute: 0, second: 0 }),
  })

  /* Start state definition */
  const [subjects, setSubjects] = useState(
    props.subjects.length > 0 ? props.subjects : [newSubject()]
  )

  // Decide which, if any, subject should be active for editing.
  let activeSubject
  if (props.activeSubject !== undefined) activeSubject = props.activeSubject
  else if (subjects.length === 1) activeSubject = 0
  else activeSubject = undefined

  const [activeIdx, setActiveIdx] = useState(activeSubject)
  const [errors, setErrors] = useState(props.errors)
  /* End state definition */

  useEffect(() => {
    setErrors(props.errors)
  }, [props.errors])

  const autosuggestText =
    props.caseType === "patientPositive" ? (
      <div className="autosuggestText">
        <p className="text-container-with-tooltip">
          What locations did this patient occupy without appropriate
          precautions?
          <ExcludedLocationsModalEnhanced />
        </p>
      </div>
    ) : (
      "What employee tested positive for an infection?"
    )

  const calendarText = (
    <div className="calendar-text">
      {props.caseType === "patientPositive" ? (
        <p className="text-container-with-tooltip">
          What time period were they in the room?
          <HelpTooltip placement="right">
            Please enter the date the patient entered and exited each location.
            You can include both patient rooms (e.g. Rm 412,) as well as
            non-patient areas (e.g. X-Ray #1) that are monitored for contact
            tracing. You can add additional locations if the patient occupied
            multiple locations during their stay.
          </HelpTooltip>
        </p>
      ) : (
        <p className="text-container-with-tooltip">
          What time period were they working in the hospital while potentially
          contagious?
          <HelpTooltip placement="right">
            Please enter the date and time the employee started their first
            shift while potentially contagious and the date and time of their
            last shift while potentially contagious.
          </HelpTooltip>
        </p>
      )}
    </div>
  )

  const additionalSubject = () => {
    const endTime = moment(subjects[subjects.length - 1].endAt)
    endTime.add({ hours: 12 })
    return {
      entityName: "",
      entityId: null,
      startAt: subjects[subjects.length - 1].endAt,
      endAt: moment.min(endTime, moment().tz(props.timezone)),
    }
  }

  /* Everything below is method definitions until we hit the return */

  const validSubjects = () =>
    subjects.filter((subject) => validateSubject(subject).length === 0).length

  // This is for editing a single subject.
  const setSubject = (idx) => (subject) => {
    setErrors([])
    const newSubjects = [...subjects]
    newSubjects[idx] = subject
    setSubjects(newSubjects)
  }

  const validateSubject = (subject) => {
    const errors = []

    if (subject.entityId === null)
      errors.push("Please complete the current record before proceeding.")

    return errors
  }

  const onAddClick = () => {
    let errors = []

    if (activeIdx !== undefined) {
      errors = validateSubject(subjects[activeIdx])
    }

    if (errors.length === 0) {
      setActiveIdx(subjects.length)
      setSubjects([
        ...subjects,
        props.usePreviousSubjectEndAt ? additionalSubject() : newSubject(),
      ])
    } else {
      setErrors(errors)
    }
  }

  const onEditClick = (idx) => () => {
    let errors = []

    if (activeIdx !== undefined) {
      errors = validateSubject(subjects[activeIdx])
    }

    if (errors.length === 0) {
      setActiveIdx(idx)
    } else {
      setErrors(errors)
    }
  }

  const onDeleteClick = (idx) => () => {
    if (idx === activeIdx) {
      setErrors([])
      setActiveIdx(undefined)
    }

    // When deleting, we want the active subject to stay active. If something is deleted that is prior in the subject
    // list, the index of our active item will change by -1.
    if (idx < activeIdx) {
      setActiveIdx(activeIdx - 1)
    }

    const newSubjects = [...subjects]
    newSubjects.splice(idx, 1)
    setSubjects(newSubjects)
  }

  const onSubmitClick = () => {
    const submitSubjects = [...subjects]

    // If the user clicked "add location" but then didn't fill out the new empty subject form, that's ok. We'll just
    // ignore it.
    if (validateSubject(submitSubjects[subjects.length - 1]).length > 0)
      submitSubjects.pop()

    props.onSubmit(submitSubjects)
  }

  return (
    <div className="CreateEditCase">
      <div className={`subjects ${subjects.length > 1 ? "multiple" : ""}`}>
        {subjects.map((subject, idx) => (
          <div
            className={`subject ${idx === activeIdx ? "active" : "inactive"}`}
            key={idx}
          >
            <div className="buttons">
              {idx !== activeIdx && (
                <button
                  className="edit"
                  type="button"
                  title="edit"
                  onClick={onEditClick(idx)}
                >
                  <i className="fa fa-pencil" />
                </button>
              )}
              {subjects.length > 1 && (
                <button
                  className="delete"
                  type="button"
                  title="edit"
                  onClick={onDeleteClick(idx)}
                >
                  <i className="fa fa-trash" />
                </button>
              )}
            </div>
            {idx === activeIdx ? (
              <CreateEditSubject
                key={idx}
                timezone={props.timezone}
                autosuggestText={autosuggestText}
                calendarText={calendarText}
                subject={subject}
                setSubject={setSubject(idx)}
                autosuggestData={props.autosuggestData}
                autosuggestPlaceholder={`Enter a ${
                  props.caseType === "patientPositive" ? "room" : "user"
                } name to search`}
              />
            ) : (
              <InactiveSubject
                idx={idx}
                onClick={onEditClick(idx)}
                subject={subject}
              />
            )}
          </div>
        ))}
      </div>
      {props.caseType === "patientPositive" && (
        <button className="add" onClick={onAddClick}>
          + add locations
        </button>
      )}
      {errors.length > 0 && (
        <div className="errors">
          {errors.map((error, idx) => (
            <div className="error" key={idx}>
              {error}
            </div>
          ))}
        </div>
      )}
      <div className={`submission ${errors.length > 0 ? "with-errors" : ""}`}>
        {props.onCancel && (
          <button className="cancel" onClick={props.onCancel}>
            Cancel
          </button>
        )}
        {props.onSubmit && (
          <button
            className="submit btn-primary"
            onClick={onSubmitClick}
            disabled={validSubjects() === 0}
          >
            {props.submitText}
          </button>
        )}
      </div>
    </div>
  )
}

CreateEditCase.propTypes = {
  caseType: PropTypes.oneOf(["patientPositive", "employeePositive"]).isRequired,
  timezone: PropTypes.string.isRequired,
  subjects: PropTypes.arrayOf(
    PropTypes.shape({
      entityName: PropTypes.string.isRequired,
      entityId: PropTypes.string.isRequired,
      startAt: MomentPropTypes.momentObj.isRequired,
      endAt: MomentPropTypes.momentObj.isRequired,
    })
  ).isRequired,
  autosuggestData: AutosuggestBar.propTypes.data,
  activeSubject: PropTypes.number,
  submitText: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  // Provide any external errors such as those experienced during the mutation.
  errors: PropTypes.arrayOf(PropTypes.string),
  usePreviousSubjectEndAt: PropTypes.bool,
}

CreateEditCase.defaultProps = {
  subjects: [],
  addButton: false,
  autosuggestData: [],
  submitText: "Save",
  errors: [],
  usePreviousSubjectEndAt: true,
}

export default CreateEditCase
